import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { dashboardApi, subscriptionsApi, emailApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  PiggyBank, 
  CreditCard, 
  AlertCircle, 
  TrendingUp,
  ArrowRight,
  Calendar,
  Plus,
  XCircle,
  RefreshCw,
  Mail
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [savingsHistory, setSavingsHistory] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [emailConnections, setEmailConnections] = useState([]);

  useEffect(() => {
    loadDashboardData();
    loadEmailConnections();
  }, []);

  const loadEmailConnections = async () => {
    try {
      const response = await emailApi.getConnections();
      setEmailConnections(response.data.connections || []);
    } catch (error) {
      console.error('Failed to load email connections:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [statsRes, historyRes, subsRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getSavingsHistory(),
        subscriptionsApi.getAll()
      ]);
      setStats(statsRes.data);
      setSavingsHistory(historyRes.data.history);
      setSubscriptions(subsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleScanAllAccounts = async () => {
    const connectedAccounts = emailConnections.filter(c => c.status === 'connected');
    
    if (connectedAccounts.length === 0) {
      toast.info('No email accounts connected. Go to Settings to connect your email.');
      return;
    }

    setIsScanning(true);
    let totalFound = 0;
    let successfulScans = 0;

    try {
      for (const connection of connectedAccounts) {
        try {
          let response;
          if (connection.provider === 'gmail') {
            response = await emailApi.scanGmail(connection.id);
          } else if (connection.provider === 'outlook') {
            response = await emailApi.scanOutlook(connection.id);
          }
          
          if (response?.data) {
            totalFound += response.data.subscriptions_created || response.data.subscriptions_detected || 0;
            successfulScans++;
          }
        } catch (err) {
          console.error(`Failed to scan ${connection.email}:`, err);
        }
      }

      if (successfulScans > 0) {
        if (totalFound > 0) {
          toast.success(`Found ${totalFound} new subscription${totalFound > 1 ? 's' : ''} across ${successfulScans} account${successfulScans > 1 ? 's' : ''}!`);
          loadDashboardData();
        } else {
          toast.info(`Scanned ${successfulScans} account${successfulScans > 1 ? 's' : ''} - no new subscriptions found.`);
        }
      } else {
        toast.error('Failed to scan email accounts. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred while scanning accounts.');
    } finally {
      setIsScanning(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active').slice(0, 5);

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              {t.dashboard.title}
            </h1>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">{t.dashboard.subtitle}</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            {emailConnections.filter(c => c.status === 'connected').length > 0 && (
              <Button 
                onClick={handleScanAllAccounts}
                disabled={isScanning}
                variant="outline"
                className="rounded-xl flex-1 sm:flex-none"
                data-testid="scan-all-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isScanning ? 'animate-spin' : ''}`} />
                {isScanning ? 'Scanning...' : 'Scan All Emails'}
              </Button>
            )}
            <Button 
              onClick={() => navigate('/add-subscription')}
              className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover flex-1 sm:flex-none"
              data-testid="add-subscription-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t.dashboard.addSubscription}
            </Button>
          </div>
        </div>

        {/* Stats Grid - Bento Layout */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
          {/* Total Savings - Large Card */}
          <Card className="col-span-2 bg-gradient-to-br from-[#064E3B] to-[#059669] text-white border-0 shadow-soft-lg">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1 sm:space-y-2">
                  <p className="text-green-100 text-xs sm:text-sm font-medium">{t.dashboard.totalSavings}</p>
                  {stats?.savings_preview ? (
                    <>
                      <div className="relative">
                        <p className="text-3xl sm:text-4xl md:text-5xl font-bold blur-sm select-none" style={{ fontFamily: 'Outfit' }}>
                          $XXX
                        </p>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="bg-white/20 px-3 py-1 rounded-full text-xs sm:text-sm font-medium backdrop-blur-sm">
                            🔒 Upgrade to view
                          </span>
                        </div>
                      </div>
                      <p className="text-green-100/80 text-xs sm:text-sm">{stats?.savings_hint}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-3xl sm:text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Outfit' }} data-testid="total-savings">
                        {formatCurrency(stats?.total_savings || 0)}
                      </p>
                      <p className="text-green-100/80 text-xs sm:text-sm">{t.dashboard.fromCancelled}</p>
                    </>
                  )}
                </div>
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 sm:w-7 sm:h-7" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Spend */}
          <Card className="border border-gray-100 shadow-soft">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1 sm:space-y-2">
                  <p className="text-gray-500 text-xs sm:text-sm font-medium">{t.dashboard.monthlySpend}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }} data-testid="monthly-spend">
                    {formatCurrency(stats?.monthly_spend || 0)}
                  </p>
                  <p className="text-xs text-gray-400">{formatCurrency(stats?.yearly_spend || 0)}/yr</p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#D1FAE5] rounded-lg sm:rounded-xl flex items-center justify-center">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-[#064E3B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Subscriptions */}
          <Card className="border border-gray-100 shadow-soft">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1 sm:space-y-2">
                  <p className="text-gray-500 text-xs sm:text-sm font-medium">{t.dashboard.active}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }} data-testid="active-count">
                    {stats?.active_subscriptions || 0}
                  </p>
                  <p className="text-xs text-gray-400">{t.dashboard.subscriptions}</p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#D1FAE5] rounded-lg sm:rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#064E3B]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Savings Chart */}
          <Card className="lg:col-span-2 border border-gray-100 shadow-soft">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                {t.dashboard.savingsOverTime}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {savingsHistory.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={savingsHistory}>
                      <defs>
                        <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#064E3B" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#064E3B" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="month" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '12px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                        }}
                        formatter={(value) => [`$${value}`, 'Savings']}
                      />
                      <Area
                        type="monotone"
                        dataKey="savings"
                        stroke="#064E3B"
                        strokeWidth={2}
                        fill="url(#savingsGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                    <TrendingUp className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">{t.dashboard.noSavingsYet}</p>
                  <p className="text-sm text-gray-400 mt-1">{t.dashboard.cancelToSave}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Renewals */}
          <Card className="border border-gray-100 shadow-soft">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  {t.dashboard.upcomingRenewals}
                </CardTitle>
                <Badge variant="outline" className="bg-[#FEF3C7] text-[#D97706] border-0">
                  {t.dashboard.next7Days}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {stats?.upcoming_renewals?.length > 0 ? (
                <div className="space-y-3">
                  {stats.upcoming_renewals.map((sub) => (
                    <div 
                      key={sub.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{sub.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(sub.renewal_date)}
                        </p>
                      </div>
                      <p className="font-semibold text-[#EA580C]">
                        ${sub.amount}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 bg-[#D1FAE5] rounded-xl flex items-center justify-center mb-3">
                    <Calendar className="w-6 h-6 text-[#064E3B]" />
                  </div>
                  <p className="text-gray-500 text-sm">{t.dashboard.noUpcoming}</p>
                  <p className="text-gray-400 text-xs mt-1">{t.dashboard.allClear}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Subscriptions */}
        <Card className="border border-gray-100 shadow-soft">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                {t.dashboard.yourSubscriptions}
              </CardTitle>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/subscriptions')}
                className="text-[#064E3B] hover:text-[#053D2E]"
                data-testid="view-all-btn"
              >
                {t.dashboard.viewAll}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {activeSubscriptions.length > 0 ? (
              <div className="space-y-3">
                {activeSubscriptions.map((sub) => (
                  <div 
                    key={sub.id}
                    className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl hover:border-[#D1FAE5] transition-colors cursor-pointer"
                    onClick={() => navigate(`/subscriptions/${sub.id}`)}
                    data-testid={`subscription-${sub.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#D1FAE5] rounded-xl flex items-center justify-center text-[#064E3B] font-semibold">
                        {sub.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{sub.name}</p>
                        <p className="text-sm text-gray-500">
                          Renews {formatDate(sub.renewal_date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        ${sub.amount}
                      </p>
                      <p className="text-xs text-gray-400">/{sub.billing_cycle}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <CreditCard className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">{t.dashboard.noSubscriptionsYet}</p>
                <p className="text-sm text-gray-400 mt-1 mb-4">{t.dashboard.addFirstSubscription}</p>
                <Button 
                  onClick={() => navigate('/add-subscription')}
                  className="bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                  data-testid="empty-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t.dashboard.addSubscription}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
